// overlayFormat.js
// Shared time/queue formatting helpers used across all /overlay/* scenes.

// ── Queue-wait model ─────────────────────────────────────────────────────────
// What a wait estimate really predicts is "time until the artist STARTS your
// pancake" — the previous one cooks while the next is drawn — so the cadence
// is roughly one draw time per queued order, not draw+cook+deliver.
// Two fixed BASELINES carry the flash-vs-custom difference; one learned
// SHIFT TEMPO multiplier (shift.drawTempoSamples, written by the artist
// view's Cooking tap) carries "how fast is the artist actually moving today".
// No samples yet → tempo 1.0 → pure baselines. Mixed queue at tempo 1.0
// averages out near Dan's observed ~7 min/pancake.
export const BASELINE_FLASH_MIN  = 5;
export const BASELINE_CUSTOM_MIN = 9;
export const TEMPO_SAMPLE_WINDOW = 5;   // last N draws inform the tempo
const TEMPO_MIN = 0.5;                  // clamp: never promise the moon…
const TEMPO_MAX = 2;                    // …never threaten the apocalypse
const WAIT_MILESTONES = [0, 7, 15, 22, 30, 37, 45, 60, 75, 90, 120];

export function baselineWaitMinutes(o) {
  const t = o?.orderType;
  if (t === "flash" || t === "freestyle_flash") return BASELINE_FLASH_MIN;
  return BASELINE_CUSTOM_MIN;
}

// Median of the shift's recent draw-tempo samples (actual draw ÷ baseline),
// clamped. Median over mean so one interrupted/suspended draw can't skew it.
export function shiftTempo(shift) {
  const raw = Array.isArray(shift?.drawTempoSamples) ? shift.drawTempoSamples : [];
  const samples = raw.filter((s) => typeof s === "number" && isFinite(s) && s > 0);
  if (samples.length === 0) return 1;
  const sorted = [...samples].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.min(TEMPO_MAX, Math.max(TEMPO_MIN, median));
}

// "mm:ss" or "h:mm:ss" — used for live timers (drawing, cooking)
export function fmtTimer(ms) {
  if (!ms || ms < 0) return "--:--";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// "1h 30m" / "5m 12s" / "45s" — used for elapsed-time labels (LastDone)
export function fmtMs(ms) {
  if (!ms) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// "2:47 PM" — static wall-clock stamp for the Just Finished slot. A finished
// pancake is finished; never tick this.
export function fmtClock(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Static milestone-based queue wait — snaps to friendly numbers, no live tick.
// Baseline weighted by orderType, scaled by the live shift tempo. `shift` is
// optional: without it (or on legacy shifts with no samples) tempo is 1.0.
export function computeQueueWait(activeOrders, shift) {
  const tempo = shiftTempo(shift);
  let total = 0;
  for (const o of activeOrders) {
    if (o.queue === "standby") continue;
    if (!["queued", "suspended", "drawing", "cooking", "flipping"].includes(o.state)) continue;
    total += baselineWaitMinutes(o) * tempo;
  }
  return Math.round(total);
}

export function fmtWait(min) {
  if (min <= 0) return "< 1 min";
  let best = WAIT_MILESTONES[0];
  for (const m of WAIT_MILESTONES) {
    if (Math.abs(m - min) <= Math.abs(best - min)) best = m;
  }
  if (best >= 120) return "~2 hr+";
  if (best >= 60) return "~1 hr";
  return `~${best} min`;
}

// Pull the right reference image for an order — flash designs use the design
// PNG, custom orders use the customer's cropped/uploaded image, freestyle has
// no image.
export function getRefSrc(order, designMap) {
  if (!order) return null;
  if (order.isFreestyle) return null;
  if (order.orderType === "flash" || order.orderType === "freestyle_flash") {
    const design = designMap[order.designId];
    return design ? design.imageUrl : null;
  }
  return order.croppedImageUrl || order.imageUrl || null;
}

export function typeLabel(order) {
  if (!order) return "";
  if (order.isFreestyle) return "✨ Freestyle";
  if (order.orderType === "magic") return "✨ Magic";
  if (order.orderType === "flash" || order.orderType === "freestyle_flash") return "⚡ Flash";
  return "🎨 Custom";
}
